// src/pages/admin/agents/UpdateAgentPage.tsx
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Users,
  Briefcase,
  AlertCircle,
  CheckCircle,
  XCircle,
  Shield,
  ChevronDown,
  ChevronRight,
  Power,
  Loader2,
} from "lucide-react";
import { alertError, alertSuccess } from "../../../helpers/alertError";
import { agentService } from "../../../services/agentService";
import { Agent, AgentModulePermission } from "../../../types/features/Agent";
import useToken from "../../../hooks/features/auth/useToken";
import useAgents from "../../../hooks/features/agents/useAgents";
import useModules from "../../../hooks/features/modules/useModules";

export default function UpdateAgentPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const agent = location.state as Agent;
  const { token } = useToken();
  const { assignPermissions } = useAgents();
  const { entrepriseModules: availableModules, getEntrepriseModules } = useModules();

  const [formData, setFormData] = useState({
    responsabilities: [] as string[],
    departement: "",
  });
  const [newResponsability, setNewResponsability] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // États pour les permissions
  const [agentPermissions, setAgentPermissions] = useState<AgentModulePermission[]>([]);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    if (agent) {
      setFormData({
        responsabilities: agent.responsabilities || [],
        departement: agent.departement || "",
      });
      loadAgentData();
      getEntrepriseModules();
    }
    setIsFetching(false);
  }, [agent]);

  const loadAgentData = async () => {
    if (!agent || !token) return;

    setLoadingPermissions(true);
    try {
      const data = await agentService.getAgentPermissions(token, agent.id);
      if (data) {
        setAgentPermissions(data.permissions || []);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des permissions", error);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const toggleModule = (moduleId: string) => {
    setExpandedModules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const handlePermissionToggle = (moduleId: string) => {
    setAgentPermissions((prev) => {
      const existing = prev.find((p) => p.module_id === moduleId);
      if (existing) {
        return prev.filter((p) => p.module_id !== moduleId);
      } else {
        const module = availableModules.find((m) => m.id === moduleId);
        return [
          ...prev,
          {
            id: "",
            agent_id: agent.id,
            module_id: moduleId,
            module_key: module?.key || "",
            module_name: module?.name || "",
            endpoints: module?.endpoints?.map((e: any) => `${e.method} ${e.link}`) || [],
            is_active: true,
            created_at: "",
            updated_at: "",
          },
        ];
      }
    });
  };

  const handleEndpointToggle = (moduleId: string, endpoint: string) => {
    setAgentPermissions((prev) => {
      return prev.map((p) => {
        if (p.module_id === moduleId) {
          const endpoints = p.endpoints || [];
          if (endpoints.includes(endpoint)) {
            return { ...p, endpoints: endpoints.filter((e) => e !== endpoint) };
          } else {
            return { ...p, endpoints: [...endpoints, endpoint] };
          }
        }
        return p;
      });
    });
  };

  const handleSavePermissions = async () => {
    if (!agent || !token) return;

    setSavingPermissions(true);
    try {
      const permissions = agentPermissions.map((p) => ({
        module_id: p.module_id,
        endpoints: p.endpoints || [],
        is_active: p.is_active,
      }));

      await assignPermissions(agent.id, permissions);
      alertSuccess("Permissions mises à jour avec succès");
      await loadAgentData();
    } catch (error) {
      alertError("Erreur lors de la mise à jour des permissions");
      console.error(error);
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleAddResponsability = () => {
    if (newResponsability.trim()) {
      setFormData({
        ...formData,
        responsabilities: [...formData.responsabilities, newResponsability.trim()],
      });
      setNewResponsability("");
    }
  };

  const handleRemoveResponsability = (index: number) => {
    setFormData({
      ...formData,
      responsabilities: formData.responsabilities.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    if (!agent?.id) {
      alertError("Une erreur est survenue");
      return;
    }

    setIsLoading(true);
    try {
      await agentService.updateAgent(token, agent.id, {
        responsabilities: formData.responsabilities,
        departement: formData.departement,
      });
      alertSuccess("Agent modifié avec succès");
      setTimeout(() => {
        navigate("/admin/agents");
      }, 1500);
    } catch (error) {
      alertError("Erreur lors de la modification de l'agent");
    } finally {
      setIsLoading(false);
    }
  };

  if (!agent && !isFetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 select-none animate-fade-in-up">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Agent non trouvé</h2>
          <p className="text-gray-500 mb-4">Les informations de l'agent sont introuvables.</p>
          <button
            onClick={() => navigate("/admin/agents")}
            className="px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 hover:shadow-md transition-all duration-300 font-medium"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8 select-none">
      {/* En-tête */}
      <div className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10 animate-fade-in-up">
        <div className="px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-300 group"
              >
                <ArrowLeft size={20} className="text-gray-600 group-hover:text-primary transition-colors" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Modifier l'agent</h1>
                <p className="text-sm text-gray-500 mt-1">{agent?.full_name || "Agent"}</p>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-sm font-medium"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Informations de l'agent */}
          <div
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up"
            style={{ animationDelay: "100ms" }}
          >
            <div className="bg-slate-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Users size={18} className="text-primary" />
                Informations de l'agent
              </h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500">Nom complet</p>
                  <p className="font-semibold text-gray-800">{agent?.full_name || "N/A"}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-semibold text-gray-800">{agent?.email || "N/A"}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500">Téléphone</p>
                  <p className="font-semibold text-gray-800">{agent?.phonenumber || "N/A"}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500">Statut</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {agent?.status === "ACTIVE" ? (
                      <>
                        <CheckCircle size={16} className="text-primary" />
                        <span className="font-semibold text-primary">Actif</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={16} className="text-red-600" />
                        <span className="font-semibold text-red-600">Inactif</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions des modules */}
          <div
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up"
            style={{ animationDelay: "150ms" }}
          >
            <div className="bg-slate-50 px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Shield size={18} className="text-primary" />
                  Permissions des modules
                </h2>
                {agentPermissions.length > 0 && (
                  <button
                    onClick={handleSavePermissions}
                    disabled={savingPermissions}
                    className="text-xs px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1"
                  >
                    {savingPermissions ? (
                      <>
                        <Loader2 size={12} className="animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      "Sauvegarder"
                    )}
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Activez les modules et sélectionnez les endpoints autorisés pour cet agent
              </p>
            </div>
            <div className="p-6">
              {loadingPermissions ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : availableModules.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  Aucun module disponible pour cette entreprise
                </p>
              ) : (
                <div className="space-y-3">
                  {availableModules.map((module) => {
                    const permission = agentPermissions.find((p) => p.module_id === module.id);
                    const isExpanded = expandedModules.has(module.id);
                    const isActive = !!permission;

                    return (
                      <div key={module.id} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleModule(module.id)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                            <span className="font-medium text-gray-800">{module.name}</span>
                            <span className="text-xs text-gray-400 font-mono">{module.key}</span>
                          </div>
                          <button
                            onClick={() => handlePermissionToggle(module.id)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                              isActive
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                            }`}
                          >
                            <Power size={14} />
                            {isActive ? "Actif" : "Inactif"}
                          </button>
                        </div>

                        {isExpanded && isActive && (
                          <div className="p-3 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-2">Endpoints autorisés :</p>
                            <div className="flex flex-wrap gap-2">
                              {(module.endpoints || []).map((endpoint: any) => {
                                const endpointKey = `${endpoint.method} ${endpoint.link}`;
                                const isChecked = permission?.endpoints?.includes(endpointKey) || false;

                                return (
                                  <label
                                    key={endpointKey}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-colors ${
                                      isChecked
                                        ? "bg-primary/10 text-primary border border-primary/30"
                                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => handleEndpointToggle(module.id, endpointKey)}
                                      className="hidden"
                                    />
                                    <span className="font-mono">{endpoint.method}</span>
                                    <span>{endpoint.link}</span>
                                    {isChecked && <CheckCircle size={12} className="text-primary" />}
                                  </label>
                                );
                              })}
                            </div>
                            {(!module.endpoints || module.endpoints.length === 0) && (
                              <p className="text-xs text-gray-400 italic">Aucun endpoint défini pour ce module</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Champs modifiables */}
          <div
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in-up"
            style={{ animationDelay: "200ms" }}
          >
            <div className="bg-slate-50 px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Briefcase size={18} className="text-primary" />
                Modifier les informations
              </h2>
              <p className="text-sm text-gray-500 mt-1">Seuls les champs ci-dessous peuvent être modifiés</p>
            </div>
            <div className="p-6 space-y-6">
              {/* Département */}
              <div>
                <label htmlFor="departement" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Département
                </label>
                <input
                  id="departement"
                  type="text"
                  value={formData.departement}
                  onChange={(e) => setFormData({ ...formData, departement: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all duration-300 bg-gray-50 focus:bg-white focus:outline-none"
                  placeholder="Ex: Support Client, Ventes, Technique..."
                />
              </div>

              {/* Responsabilités */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Responsabilités</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newResponsability}
                    onChange={(e) => setNewResponsability(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddResponsability()}
                    className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all duration-300 bg-gray-50 focus:bg-white focus:outline-none"
                    placeholder="Ajouter une responsabilité..."
                  />
                  <button
                    onClick={handleAddResponsability}
                    className="px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all duration-300 font-medium"
                  >
                    Ajouter
                  </button>
                </div>

                {formData.responsabilities.length > 0 ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {formData.responsabilities.map((resp, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-xl text-sm font-medium"
                      >
                        {resp}
                        <button
                          onClick={() => handleRemoveResponsability(index)}
                          className="hover:text-red-600 transition-colors"
                        >
                          <XCircle size={16} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-sm text-gray-400 italic">Aucune responsabilité ajoutée</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
}